import { httpsCallable } from 'firebase/functions'
import { runInAction } from 'mobx'
import { RoomStateVideoMethod } from '../../../types'
import type {
  SlideDeckInclude,
  GetSlideDeckForAssignmentResult,
} from '../../../types/slideDeckAccess'
import {
  populateModelsFromApiResponse,
  convertApiDocsToModels,
} from '../../../firestore-mobx/apiModelHelper'
import { SlideCaption } from '../../../models/SlideCaption'
import type { FirestoreSlideCaption } from '../../../firestore/SlideCaption/schema'
import { SlideModel } from '../../../models/SlideModel'
import { SlideQuestion } from '../../../models/SlideQuestion'
import { SlideDeckExhibit } from '../../../models/SlideDeckExhibit'
import { SlideRubric } from '../../../models/SlideRubric'
import { SlideDeckRole } from '../../../models/SlideDeckRole'
import { captureException } from '@sentry/core'
import type { PreviewMeetingCubit } from '../../PreviewMeetingCubit'
import { MeetingDataFetcher } from '../MeetingDataFetcher'

/**
 * Data fetcher for slide deck previews.
 *
 * Uses the getSlideDeckForAssignment API to fetch slide deck data with
 * signed URLs. This ensures codeless access students can view assets.
 *
 * - Instructors/TAs/admins: pass slideDeckId only (API allows by role)
 * - Students: must provide sectionId + assignmentId (API requires for
 *   student auth — payment/access checks)
 */
export class PreviewDataFetcher extends MeetingDataFetcher {
  previewMeeting: PreviewMeetingCubit
  constructor(meeting: PreviewMeetingCubit) {
    super(meeting)
    this.previewMeeting = meeting
  }

  startedPreviewFetcher: boolean = false
  async start() {
    if (this.startedPreviewFetcher) {
      console.warn('PreviewDataFetcher already started, skipping start.')
      return
    }
    this.startedPreviewFetcher = true
    const slideDeckId = this.previewMeeting._slideDeckId
    // if we don't have a slide deck id we must have a room ID
    // so call start on the parent data fetcher to get all the room
    // state data
    if (slideDeckId === undefined) {
      return super.start()
    }
    if (this.meeting.useMockResults) {
      this.meeting.roomState.replaceData({
        activeExhibitId: undefined,
        activeSlide: 0,
        activeSlideChangedAt: new Date('Thu May 23 2024 15:18:31 GMT'),
        roomStartedAt: new Date('Thu May 23 2024 14:18:31 GMT'),
        groupLeaderUserIds: ['1'],
        userIds: ['1', '2', '3'],
        slideDeckId: slideDeckId,
        scheduledAt: new Date('Thu May 23 2024 14:00:00 GMT'),
        sectionId: '',
        updatedAt: new Date(),
        hiddenUserIds: [],
        videoMethod: RoomStateVideoMethod.streaming,
      })
    }

    this.fetchSlideDeck(slideDeckId)
    this.fetchPreviewDataViaApi(slideDeckId)
    this.fetchSettingsProcessingVideo()
    this.fetchDefaultProcessingAsset()
    this.addCaptionsReaction()
  }

  /**
   * Fetch slides, questions, exhibits, and roles via the API.
   * Uses signed URLs so codeless access students can load assets.
   */
  private async fetchPreviewDataViaApi(slideDeckId: string) {
    try {
      const sectionId = this.previewMeeting._previewSectionId
      const assignmentId = this.previewMeeting._previewAssignmentId

      const getSlideDeckForAssignment = httpsCallable<
        {
          sectionId?: string
          assignmentId?: string
          slideDeckId: string
          include: SlideDeckInclude[]
          databaseId?: string
        },
        GetSlideDeckForAssignmentResult
      >(this.repository.functions, 'getSlideDeckForAssignment')

      const { data: result } = await getSlideDeckForAssignment({
        ...(sectionId && { sectionId }),
        ...(assignmentId && { assignmentId }),
        slideDeckId,
        include: [
          'slides',
          'questions',
          'exhibits',
          'roles',
          'slideCaptions',
          'rubrics',
        ],
        ...(this.repository.databaseId && {
          databaseId: this.repository.databaseId,
        }),
      })

      if (!result.success) {
        runInAction(() => {
          this.meeting.slideDeckSlides.replaceModels([])
          this.meeting.slideDeckQuestions.replaceModels([])
          this.meeting._slideDeckExhibits.replaceModels([])
          this.meeting.roles.replaceModels([])
          this.meeting.apiRubrics.replaceModels([])
        })
        return
      }

      runInAction(() => {
        populateModelsFromApiResponse(this.repository, result, [
          {
            key: 'slides',
            modelClass: SlideModel,
            target: this.meeting.slideDeckSlides,
          },
          {
            key: 'questions',
            modelClass: SlideQuestion,
            target: this.meeting.slideDeckQuestions,
          },
          {
            key: 'exhibits',
            modelClass: SlideDeckExhibit,
            target: this.meeting._slideDeckExhibits,
          },
          {
            key: 'roles',
            modelClass: SlideDeckRole,
            target: this.meeting.roles,
          },
          {
            key: 'rubrics',
            modelClass: SlideRubric,
            target: this.meeting.apiRubrics,
          },
        ])

        // Index API captions by slideId for use in resetCaptions()
        if (result.slideCaptions) {
          const allCaptions = convertApiDocsToModels(
            this.repository,
            result.slideCaptions as {
              id: string
              data: FirestoreSlideCaption
            }[],
            SlideCaption
          )
          this.apiCaptionsBySlideId = new Map()
          for (const caption of allCaptions) {
            const sid = caption.data.slideId
            const existing = this.apiCaptionsBySlideId.get(sid) ?? []
            existing.push(caption)
            this.apiCaptionsBySlideId.set(sid, existing)
          }
        }
      })
    } catch (e) {
      runInAction(() => {
        this.meeting.slideDeckSlides.replaceModels([])
        this.meeting.slideDeckQuestions.replaceModels([])
        this.meeting._slideDeckExhibits.replaceModels([])
        this.meeting.roles.replaceModels([])
        this.meeting.apiRubrics.replaceModels([])
      })
      captureException(e, { tags: { feature: 'preview-slide-deck-api' } })
    }
  }

  fetchUsers(): Promise<void> {
    return Promise.resolve()
  }

  listenToQuestionAnswers() {
    return Promise.resolve()
  }

  getUserAnswers(): Promise<void> {
    return Promise.resolve()
  }

  getGroupAnswers(): Promise<void> {
    return Promise.resolve()
  }
}
